import { IEUVA_Checkout_Params } from '../interfaces/ieuva_checkout_params';
import { IDictionary } from '../interfaces/idictionary';
export class Checkout {
	/**
	 * The singleton instance of this class.
	 *
	 * @var Checkout
	 */
	protected static _instance;

	/**
	 * The settings passed to the class.
	 *
	 * @var IEUVA_Checkout_Params
	 */
	protected settings: IEUVA_Checkout_Params;

	/**
	 * The "VAT number field" HTML element displayed on the checkout page.
	 *
	 * Note
	 * Despite WooCommerce calling it "field", this is the wrapper element
	 * around the actual <input> field.
	 *
	 * @var HTMLElement
	 */
	protected static vat_number_field: HTMLElement;

	/**
	 * The "VAT number" HTML <input> element displayed on the checkout page,
	 * containing the actual VAT number. This element is wrapped within the
	 * "vat_number_field" element.
	 *
	 * @var HTMLInputElement
	 * @see Checkout.vat_number_field
	 */
	protected static vat_number_input: HTMLInputElement;

	/**
	 * The "self certificatin field" HTML element displayed on the checkout page.
	 *
	 * Note
	 * Despite WooCommerce calling it "field", this is the wrapper element
	 * around the actual <input> field.
	 *
	 * @var HTMLElement
	 */
	protected static self_certification_field: HTMLElement;


	/**
	 * Stores the last VAT number processed. This is used to prevent
	 * validating the same VAT number twice.
	 *
	 * @var string
	 */
	private last_vat_number: string;

	/**
	 * Returns the selector to be used to intercept the "change" event
	 * on the checkout form.
	 *
	 * @returns string
	 */
	protected get_onchange_event_selector(): string {
		let event_selector = '';
		switch(this.settings.tax_based_on) {
			case 'billing':
				event_selector = 'select#billing_country';
				break;
			case 'shipping':
				event_selector = 'select#billing_country, select#shipping_country, input#ship-to-different-address-checkbox';
				break;
			default:
				event_selector = 'select#billing_country';
		}
		return event_selector + ', #billing_company, #vat_number';
	}

	/**
	 * Returns  the country that will be used for tax calculation.
	 *
	 * @returns string
	 */
	protected get_vat_country(): string {
		let country_field_selector = '#billing_country';
		if((this.settings.tax_based_on === 'shipping') && jQuery('#ship-to-different-address input[type="checkbox"]').prop('checked')) {
			country_field_selector = '#shipping_country';
		}

		return jQuery(country_field_selector).val();
	}

	/**
	 * Indicates if the VAT number field must be hidden (the field could have to
	 * be hidden because the customer in in shop's base country, and the admin
	 * configured the system to hide the VAT field in such case.
	 *
	 * @param string country
	 * @returns boolean
	 */
	protected must_hide_vat_number_field_for_base_country(country: string): boolean {
		return !this.settings.show_eu_vat_number_for_base_country &&
						(country == this.settings.shop_base_country);
	}

	/**
	 * Indicates there is a VAT number validator available for a country.
	 *
	 * @param {string} country
	 */
	protected country_has_vat_number_validator(country: string): boolean {
		return this.settings.vat_number_countries.includes(country);
	}

	/**
	 * Indicates if the specified country is part of the EU.
	 *
	 * @param {string} country
	 */
	protected is_eu_vat_country(country: string): boolean {
		return this.settings.eu_vat_countries.includes(country);
	}

	/**
	 * Indicates if the VAT number field should be displayed. The field is
	 * displayed if the following criteria is satisfied:
	 * - The specified country belongs to the list of countries for which
	 *   VAT numbers can be handled
	 * AND
	 * - If the country matches shop's base country, option "show VAT number
	 *   for base country" is DISABLED
	 *
	 * @param {string} country
	 * @returns boolean
	 */
	protected should_show_vat_number_for_country(country: string): boolean {
		// Show it for all countries for which VAT numbers can be
		// handled, except base country
		return !this.must_hide_vat_number_field_for_base_country(country) &&
					 this.country_has_vat_number_validator(country);
	}

	/**
	 * Indicates if the EU VAT field is required. The field is normally optional,
	 * but it can be required in several cases:
	 * - If it's configured as "required".
	 * - If it's configured as "required for the EU" and the billing country is in
	 *   the EU.
	 * - If it's configured as "required only if company is filled" and the company
	 *   field is filled.
	 *
	 * @param string country The VAT country selected at checkout.
	 * @returns boolean
	 * @since 1.5.6.151230
	 */
	protected is_vat_number_required(country): boolean {
		// If the VAT number field must be hidden, then it cannot be required
		if(this.must_hide_vat_number_field_for_base_country(country)) {
			return false;
		}

		const company_field = document.getElementById('billing_company');
		const company_name = (company_field ? company_field['value'].trim() : '');
		let company_field_filled = (company_name.length > 0);
		let is_eu_vat_country = this.is_eu_vat_country(country);

		let result =
			// Required in all cases
			(this.settings.vat_number_field_required === 'required') ||
			// Required for EU only, and country is in the EU
			((this.settings.vat_number_field_required === 'required_for_eu_only') && is_eu_vat_country) ||
			// Required if company is filled, and company is filled
			((this.settings.vat_number_field_required === 'required_if_company_field_filled') && company_field_filled) ||
			// Required if company is filled, and company is filled, but only for EU
			((this.settings.vat_number_field_required === 'required_if_company_field_filled_eu_only') && company_field_filled && is_eu_vat_country);

		return result;
	}

	/**
	 * Shows or hides the VAT number field, depending on the data entered
	 * on the checkout page.
	 *
	 * @returns void
	 */
	public update_vat_number_field_visibility(): void {
		let vat_country = this.get_vat_country();
		let vat_number_required = this.is_vat_number_required(vat_country);
		let $vat_number_field = jQuery(Checkout.vat_number_field);

		// Hide or show the EU VAT element, depending on the country
		// @see Checkout.should_show_vat_number_for_country()
		if(vat_country &&
				this.should_show_vat_number_for_country(vat_country) ||
				// Show VAT number if it's required
				vat_number_required
			) {
			$vat_number_field.fadeIn();
		}
		else {
			$vat_number_field.fadeOut();
			Checkout.vat_number_input.value = '';
		}

		// Flag the VAT number field as "required", via CSS, if needed. This will allow
		// WooCommerce to highlight it with a red border if it's required and
		// left empty
		// @since 1.5.6.151230
		if($vat_number_field.is(':visible') && vat_number_required) {
			$vat_number_field.find('.form-row').addClass('validate-required');
		}
		else {
			$vat_number_field.find('.form-row').removeClass('validate-required');
		}
	}

	/**
	 * Sets the handlers required for the validation of the VAT number field.
	 *
	 * @returns void
	 */
	public set_vat_number_field_handlers(): void {
		var checkout_handler = this;
		jQuery(this.get_onchange_event_selector()).on('change', () => {
			// Hide or show the VAT number, depending on the selected country
			checkout_handler.update_vat_number_field_visibility();
			// Validate the VAT number, if entered. This call also updates the visibility of the self-certification
			// field, if that field is displayed
			checkout_handler.validate_vat_number(checkout_handler.get_vat_country(), Checkout.vat_number_input.value);
		});

		// TODO Verify the possibility of validating the VAT number when the "update_checkout" event is triggered.
		// IMPORTANT: check that this handling would not result in infinite loops, or multiple unnecessary checks in a row.
		// jQuery(document.body).on('update_checkout', () => {
		// 	checkout_handler.validate_vat_number(checkout_handler.get_vat_country(), Checkout.vat_number_input.value);
		// });

		// Validate the VAT number on the fly, as the customer types in
		jQuery(Checkout.vat_number_input).on('blur', () => {
			checkout_handler.validate_vat_number(checkout_handler.get_vat_country(), Checkout.vat_number_input.value);
		});
	}

	/**
	 * Sets the handlers required to show or hide the self-certification field.
	 *
	 * @returns void
	 * @since 2.0.8.210112
	 */
	public set_self_certification_field_handlers(): void {
		var checkout_handler = this;

		// Update the display of the self certification element when the checkout form changes
		jQuery(this.get_onchange_event_selector()).on('change', function() {
			checkout_handler.update_self_certification_field();
		});

		// Update the display of the self certification element when the VAT number changes (even if invalid)
		jQuery(document).on('wc_aelia_euva_eu_vat_number_validation_number_incomplete', function(event, country, vat_number) {
			checkout_handler.update_self_certification_field();
		});

		// Update the display of the self certification element when the VAT number has been validate
		jQuery(document).on('wc_aelia_euva_eu_vat_number_validation_complete', function(event, validation_response) {
			checkout_handler.update_self_certification_field();
		});
	}

	/**
	 * Constructor.
	 *
	 * @param settings IEUVA_Checkout_Params
	 */
	public constructor(settings: IEUVA_Checkout_Params) {
		this.settings = settings;

		// Store the instance of the class, so that it can be fetched
		// later by calling Checkout.instance()
		// @since 2.0.0.201201
		Checkout._instance = this;
	}

	/**
	 * Returns the instance of this class.
	 *
	 * @returns Checkout
	 */
	public static instance(): Checkout {
		// If the class has not been instantiated, throw an exception. The handler must be
		// initialised first, as it needs a couple of settings to get ready.
		// @since 2.0.0.201201
		if(!Checkout._instance) {
			throw new Error('Checkout handler has not been instantiated. Please call `Checkout.init(<settings>)` before calling `Checkout.instance()`.');
		}
		return Checkout._instance;
	}

	/**
	 * Initialises the class and its event handlers.
	 *
	 * @param settings IEUVA_Checkout_Params
	 * @return void
	 */
	public static init(settings: IEUVA_Checkout_Params): void {
		if(document.querySelectorAll('.woocommerce form.checkout').length <= 0) {
			console.log('Checkout form not found. EU VAT Assistant scripts not loaded.');
			return;
		}

		// Initialise the Checkout handler. We can't call Checkout.instance here,
		// as the handler must be initialised with the settings first
		let checkout_handler = new Checkout(settings);

		// Set the handlers for the VAT number field, if present
		if((Checkout.vat_number_field = document.getElementById('vat_number_field')) &&
			 (Checkout.vat_number_input = (document.getElementById('vat_number') as HTMLInputElement))) {
				checkout_handler.set_vat_number_field_handlers();
		}

		// Set the handlers for the self-certification field, if present
		// @since 1.15.0.201125
		if(Checkout.self_certification_field = document.getElementById('customer_location_self_certified_field')) {
			let $self_certification_description = jQuery(Checkout.self_certification_field).find('.description');

			// Display the description of the self certification field before the checkbox
			jQuery(Checkout.self_certification_field).find('.woocommerce-input-wrapper').before($self_certification_description);
			checkout_handler.update_self_certification_field();

			// @since 2.0.8.210112
			checkout_handler.set_self_certification_field_handlers();
		}

		// Trigger an update of the checkout form to display the EU VAT field
		jQuery('select#billing_country').change();
	}

	/**
	 * Validates the VAT number entered by the customer and updates the checkout
	 * page according to the result.
	 *
	 * @param {string} country
	 * @param {string} vat_number
	 */
	protected validate_vat_number(country: string, vat_number: string): void {
		// Remove the country prefix, if present
		if(vat_number.includes(country)) {
			vat_number = vat_number.slice(country.length);
		}

		// Remove whitespace, to reduce payload and avoid checking the same number twice,
		// just because it has extra spaces
		vat_number = vat_number.trim();

		// If the number has not changed since last time, skip the validation
		if((country + vat_number) === this.last_vat_number) {
			return;
		}

		// Reset the "invalid value" CSS from the VAT number, before retying the validation
		// @since 2.0.14.210317
		jQuery(Checkout.vat_number_field).removeClass('woocommerce-invalid');

		// Don't bother sending an Ajax request when the required values are
		// empty
		if((country === '') || (vat_number.trim() === '')) {
			// Set the VAT number as "invalid". It can't be valid, if either the
			// country or the number is empty
			// @since 1.9.2.181212
			jQuery(Checkout.vat_number_input).prop('valid', false);

			// Only display an empty VAT number as "invalid" if it's
			// required
			// @since 1.9.3.181217
			if(this.is_vat_number_required(country)) {
				jQuery(Checkout.vat_number_field).addClass('woocommerce-invalid');
			}

			jQuery(document).trigger('wc_aelia_euva_eu_vat_number_validation_number_incomplete', country, vat_number);

			return;
		}

		// Store the last validated number
		this.last_vat_number = country + vat_number;

		let ajax_args = {
			'action': 'validate_vat_number',
			'country': country,
			'vat_number': vat_number,
			'_ajax_nonce': this.settings._ajax_nonce,
		}

		jQuery.get(this.settings.ajax_url, ajax_args, function(response) {
			// Tag the field to indicate if it's valid or not
			var vat_number_valid = response ? response.valid : false;
			jQuery(Checkout.vat_number_input).prop('valid', vat_number_valid);

			// Highlight the VAT number field when the entered VAT number is not valid
			// @since 1.8.4.181009
			jQuery(Checkout.vat_number_field).toggleClass('woocommerce-invalid',  !vat_number_valid);

			// Trigger an event upon completion of validation
			// @since 1.7.15.171106
			jQuery(document).trigger('wc_aelia_euva_eu_vat_number_validation_complete', response);
		})
		// Track unexpected errors in the VAT validation request
		// @since 1.9.0.181022
		.fail(function(response){
			console.log("VAT validation request failed", response);
		});
	}

	/**
	 * Shows/hides the self-certification field, depending on the data entered on
	 * checkout page and the plugin settings.
	 */
	public update_self_certification_field(): void {
		// If the self certification field is not present, stop here
		if(!Checkout.self_certification_field) {
			return;
		}

		// By default, assume that we should show the self-certification element
		let show_element = true;
		// If VAT number is valid, and option "hide self-certification when
		// VAT number is valid" is enabled, the self-certification field must
		// be hidden. It will be ignored at checkout
		if(jQuery(Checkout.vat_number_input).prop('valid') && (this.settings.hide_self_cert_field_with_valid_vat == 1)) {
			show_element = false;
		}
		else {
			switch(this.settings.show_self_cert_field) {
				case 'yes':
					// No action needed, take the initial value
					break;
				case 'conflict-only':
					show_element = !this.sufficient_location_evidence();
					break;
			}
		}

		// Replace tokens in the self certification box, if any
		let self_cert_label = this.settings.user_interface['self_certification_field_title'];
		jQuery(Checkout.self_certification_field)
			.find('.self_certification_label')
			.html(self_cert_label.replace('{billing_country}', jQuery('#billing_country option:selected').text()));

		if(show_element) {
			jQuery(Checkout.self_certification_field).fadeIn();
		}
		else {
			jQuery(Checkout.self_certification_field).fadeOut();
		}
	}

	/**
	 * Determines if there is enough evidence about customer's location to satisfy
	 * EU requirements.
	 *
	 * @returns boolean
	 */
	private sufficient_location_evidence(): boolean {
		let country_count: IDictionary<number> = {};

		const billing_country = jQuery('#billing_country').val();
		country_count[billing_country] = (country_count[billing_country] ?? 0) + 1;

		// Take shipping country as evidence only if explicitly told so
		if(this.settings.use_shipping_as_evidence) {
			if(jQuery('#ship-to-different-address-checkbox').is(':checked') && (jQuery('#shipping_country').length > 0)) {
				const shipping_country = jQuery('#shipping_country').val();
				country_count[shipping_country] = (country_count[shipping_country] ?? 0) + 1;
			}
		}
		// Increase the count for the country matching customer's IP address
		country_count[this.settings.ip_address_country] = (country_count[this.settings.ip_address_country] ?? 0) + 1;

		for(let country_id in country_count) {
			// We have sufficient evidence as long as we have at least a count of 2
			// for any given, non empty country
			if((country_id != '') && (country_count[country_id] >= 2)) {
				return true;
			}
		}
		return false;
	}
}